import { getFirebase } from '@/services/firebaseApp'

import { pendingLink } from '@/features/Auth/session/pendingLink'
import { Credential, ProviderFailure, ProviderId } from './types'

/**
 * The web SDK signs in and hands back a credential in the same call.
 *
 * This is the one place the two apps genuinely diverge. On mobile a provider asks its native
 * SDK for a credential and `exchange` redeems it; `signInWithPopup` does both at once, so
 * there is nothing left to redeem and the provider returns a credential of kind `session`.
 * The state names, the provider ids and the events are unchanged — what differs is which
 * step the popup collapses, and it is collapsed here rather than in five places. See
 * EPIC-005.
 *
 * Because the sign-in happens inside the popup, so does every failure it can produce, which
 * is why cancellation and the already-registered-address conflict are recognised here and
 * not in `exchange`.
 */

// Closing the popup, or opening a second one over the first, is someone backing out. Neither
// is an error to report, and both used to surface as a generic "something went wrong".
const CANCELLED_CODES = [
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
  'auth/popup-blocked'
]

const PROVIDER_NAMES: Record<string, string> = {
  'google.com': 'Google',
  'apple.com': 'Apple',
  'facebook.com': 'Facebook',
  password: 'email'
}

/**
 * Which providers this address already works through, named for a person to read.
 *
 * Returned rather than phrased: turning it into a sentence needs the locale, and a provider
 * has no business loading the app's i18n. The bridge renders it — knowing which of four to
 * try is the actionable half, and the only half worth fetching for.
 */
async function existingProviders(email?: string) {
  if (!email) return undefined

  try {
    const firebase = await getFirebase()
    const methods = await firebase.auth().fetchSignInMethodsForEmail(email)

    return methods.map((method: string) => PROVIDER_NAMES[method] ?? method).join(', ') || undefined
  } catch {
    // A courtesy; failing to fetch it must not replace the conflict with a different error.
    return undefined
  }
}

type OAuthProvider = { addScope?: (scope: string) => void }

export async function signInWithPopup(
  provider: ProviderId,
  build: (firebase: any) => OAuthProvider
): Promise<Credential> {
  const firebase = await getFirebase()

  let result

  try {
    result = await firebase.auth().signInWithPopup(build(firebase))
  } catch (error) {
    const code = (error as { code?: string })?.code

    if (CANCELLED_CODES.includes(code)) {
      throw new ProviderFailure(provider, 'cancelled', `${provider} sign-in was cancelled`, { cause: error })
    }

    if (code === 'auth/account-exists-with-different-credential') {
      // Held rather than discarded. Signing in the way this address already works will
      // attach it, which is how someone connects a second provider to an account they
      // already have — dropping it here removed that capability entirely, for every
      // provider except Facebook, which merely showed an alert and dropped it too.
      const held = (error as { credential?: unknown })?.credential
      const email = (error as { email?: string })?.email

      if (held) {
        pendingLink.hold({ provider, credential: { kind: 'firebase', provider, credential: held } })
      }

      throw new ProviderFailure(provider, 'conflict', await existingProviders(email), { cause: error })
    }

    throw new ProviderFailure(provider, 'rejected', `${provider} refused the sign-in`, { cause: error })
  }

  const user = result?.user

  if (!user?.uid) {
    throw new ProviderFailure(provider, 'rejected', `${provider} returned no signed-in user`)
  }

  return {
    kind: 'session',
    provider,
    uid: user.uid,
    extras: {
      // Facebook returns the address on the provider record rather than on the user.
      email: user.email ?? user.providerData?.[0]?.email ?? undefined,
      displayName: user.displayName ?? undefined
    }
  }
}
